import { SetupRequirementsCards } from '@/components/SetupRequirementsCards'
import { AdminPageHeader } from '@/components/patterns/admin-page'
import CreateActionButton from '@/components/patterns/create-action-button'
import TerminalCard from '@/components/pos/TerminalCard'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { Can } from '@/lib/casl'
import { ensurePosSetupRequirements, usePosSetupRequirements } from '@/lib/pos/setup-requirements'
import { locationFilters } from '@/lib/services/locations/locations.query'
import { assertNotSalesDemoOrganization } from '@/lib/services/sales-demos/sales-demos.guard'
import { useTerminals } from '@/lib/services/terminals/terminals.query'
import { createFileRoute, Link, notFound, useParams } from '@tanstack/react-router'
import { CreditCard } from 'lucide-react'
import {
  terminalSearchSchema,
  assertTerminalAccountAccess,
} from '@/lib/services/terminals/terminals.schema'
import { stripeCardReadersLink, stripeUpgradeLink } from '@/lib/stripe-financial-routes'
import { hasDashboardlessStripeTools } from '@/components/stripe-connect/stripeConnectStatus'
import { useStripeConnectStatus } from '@/lib/services/stripe-connect/stripe-connect.query'
import { InfoNotice } from '@/components/patterns/info-notice'

const terminalLocationFilters = locationFilters().applyFilter({ active: true }).get()

function TerminalsPageHeader({ actions = null }) {
  return (
    <AdminPageHeader
      icon={CreditCard}
      title="Card Readers"
      description="Register card readers or reset one stuck on a payment screen."
      actions={actions}
    />
  )
}

export const Route = createFileRoute('/_authenticated/$orgId/settings/terminals/')({
  component: RouteComponent,
  validateSearch: terminalSearchSchema,
  loaderDeps: ({ search }) => ({ accountContext: search.accountContext }),
  loader: ({ context, params, deps = {} }) => {
    if (!context.ability.can('list', 'terminals')) throw notFound()
    assertTerminalAccountAccess(context.ability, deps.accountContext)

    assertNotSalesDemoOrganization(context.queryClient, params.orgId)
    if (!deps.accountContext || deps.accountContext === 'current') {
      void ensurePosSetupRequirements(context.queryClient, {
        organizationId: params.orgId,
        locationFilterParams: terminalLocationFilters,
      })
    }
    return { isReadOnly: !context.ability.can('edit', 'terminals') }
  },
})

function RouteComponent() {
  const { accountContext } = Route.useSearch()
  // Retained reader management is not a new POS payment. Its API resolves and
  // authorizes the selected account even if the current account needs attention.
  return accountContext && accountContext !== 'current' ? (
    <TerminalsPageContent />
  ) : (
    <CurrentAccountTerminals />
  )
}

function CurrentAccountTerminals() {
  const { orgId } = useParams({ strict: false })
  const { isReady, showLocationsCard, showStripeCard } = usePosSetupRequirements({
    organizationId: orgId,
    locationFilterParams: terminalLocationFilters,
  })

  if (!isReady) {
    return (
      <div className="space-y-4">
        <TerminalsPageHeader />
        <SetupRequirementsCards
          showLocationsCard={showLocationsCard}
          showStripeCard={showStripeCard}
          locationsDescription="You need to add at least one location before you can register and use POS terminals."
          stripeDescription="You need to configure Stripe Connect before you can register and use POS terminals. Set it up from Stripe in Settings."
        />
      </div>
    )
  }

  return <TerminalsPageContent />
}

function BuyCardReadersButton() {
  const { orgId } = useParams({ strict: false })
  const { data: stripeConnectStatus } = useStripeConnectStatus({ organizationId: orgId })

  if (!hasDashboardlessStripeTools(stripeConnectStatus)) {
    return null
  }

  return (
    <Button variant="outline" asChild>
      <Link {...stripeCardReadersLink(orgId)}>Buy Card Readers</Link>
    </Button>
  )
}

function TerminalsPageContent() {
  const { accountContext } = Route.useSearch()
  const { data: terminals = [] } = useTerminals({ accountContext })
  const { orgId } = useParams({ strict: false })
  const pathHelper = usePathHelper()
  const migrationContext = accountContext && accountContext !== 'current'

  const createPath = pathHelper.terminals?.create?.()

  const showPath = (id) =>
    pathHelper.terminals?.show?.(id) || {
      link: { to: '/$orgId/settings/terminals/$terminalId', params: { orgId, terminalId: id } },
    }

  return (
    <div className="space-y-4">
      <TerminalsPageHeader
        actions={
          !migrationContext ? (
            <>
              <Can I="edit" a="stripe-account">
                <BuyCardReadersButton />
              </Can>
              <Can I="create" a="terminals">
                <CreateActionButton asChild>
                  <Link {...createPath.link}>Register New Terminal</Link>
                </CreateActionButton>
              </Can>
            </>
          ) : (
            <Button variant="outline" asChild>
              <Link {...stripeUpgradeLink(orgId)}>Back to account upgrade</Link>
            </Button>
          )
        }
      />
      {migrationContext ? (
        <InfoNotice
          title={accountContext === 'source' ? 'Original account readers' : 'New account readers'}
          compact
        >
          Reader actions apply only to this account. Use account upgrade to register each original
          device on the new account.
        </InfoNotice>
      ) : null}

      {terminals.length > 0 ? (
        <div className="flex flex-row gap-6 flex-wrap">
          {terminals.map((terminal) => (
            <div className="max-w-lg w-full" key={terminal.id}>
              <Can do="view" on="terminals" passThrough>
                {(allowed) =>
                  allowed ? (
                    <TerminalCard
                      terminal={terminal}
                      {...showPath(terminal.id).link}
                      search={{ accountContext }}
                    />
                  ) : (
                    <TerminalCard terminal={terminal} disabled={true} />
                  )
                }
              </Can>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Typography variant="supporting">No terminals registered.</Typography>
          {!migrationContext ? (
            <div className="mt-4">
              <Link {...createPath.link}>
                <Button variant="outline">
                  <Typography variant="label">Register your first terminal</Typography>
                </Button>
              </Link>
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}
